/**
 * This module detects if the user is currently actively using the 
 * application.
 * 
 * For this, it looks whether the browser tab is visible or hidden using the 
 * page visibility api, and it also looks for things like mouse movement and 
 * typing on the page.
 * 
 * It emits 'user-activity-status-change' events on the window which can be
 * listened to by registering an event listener.
 * 
 * Example:
 * 
 *  window.addEventListener("user-activity-status-change", (e) => { 
 *      console.log("userActive: " + e.detail.userActive);
 *  });
 * 
 * The public properties pageVisible and userActive can also directly be
 * accessed.
 */
class BrowserTabActivityService {

    // static configuration ___________________________________________________

    debug = false;
    test = false;
    IDLE_TIME = 5000; // 5 seconds of inactivity turns userActive off

    // public members _________________________________________________________

    pageVisible = !document.hidden;
    userActive = undefined; // if we put true, then the initial trigger won't happen

    // private members ________________________________________________________

    #endOfUserActivityTimer; // timer that waits a certain period to toggle user
                             // activity off if no activity detected for IDLE_TIME

    #lastUserActivity; // used for debouncing the code
    #onUserActivityRef; // reference to be able to remove the even listener
    #onVisibilityChangeRef; // reference to be able to remove the even listener
    #onUserActivityChangeRef; // reference to be able to remove the even listener

    //-------------------------------------------------------------------------

    onVisibilityChange() {
        this.pageVisible = !document.hidden;
        if (this.debug)
          console.log("pageVisible: " + this.pageVisible)

        if (this.pageVisible)
            this.onUserActivity();
        else {
            if (this.userActive) {
                this.userActive = false;
                this.dispatchUserActivityEvent();
            }
            // if a timer is already running, cancel it
            this.cancelTimer();
        }
    }

    onUserActivity() {
        const currentTimeMillis = (new Date()).getTime();

        // debounce
        if (this.userActive && (currentTimeMillis - this.#lastUserActivity) < 2000) 
            return;
        this.#lastUserActivity = currentTimeMillis;

        if (!this.userActive) {
            this.userActive = true;
            this.dispatchUserActivityEvent();
        }

        if (this.debug)
            console.log("User activity detected");
        // if user is active, set a timer to check if he's still active in IDLE_TIME from now
        // if not, turn the status back to inactive
        this.startTimer();
    }

    //-------------------------------------------------------------------------

    inactivityTimerTriggers() {
        if (this.debug)
            console.log("Inactivity timer triggered");
        const currentTimeMillis = (new Date()).getTime();
        if ((currentTimeMillis - this.#lastUserActivity) > this.IDLE_TIME) {
            this.userActive = false;
            this.dispatchUserActivityEvent();
            this.#endOfUserActivityTimer = null;
        }
    }

    startTimer() {
        this.cancelTimer();
        if (this.debug)
            console.log("Start inactivity timer");

        this.#endOfUserActivityTimer = setTimeout(this.inactivityTimerTriggers.bind(this), this.IDLE_TIME);
    }

    cancelTimer() {
        // if a timer is already running, cancel it
        if (this.#endOfUserActivityTimer)
            clearTimeout(this.#endOfUserActivityTimer);
        this.#endOfUserActivityTimer = null;
    }

    dispatchUserActivityEvent() {
        window.dispatchEvent(new CustomEvent("user-activity-status-change", { detail: { userActive: this.userActive }}))
     }

    // test listener to see if it works
    onUserActivityChange(e) {
        console.log("UserActive: " + e.detail.userActive)
    }

    setup() {
        if (this.debug)
            console.log("browser-tab-activity.service setup");

        //
        // tab visibility 
        //
        let newOnVisibilityChangedListener = this.onVisibilityChange.bind(this);

        document.removeEventListener("visibilitychange", this.#onVisibilityChangeRef);
        document.addEventListener("visibilitychange", newOnVisibilityChangedListener);
        this.#onVisibilityChangeRef = newOnVisibilityChangedListener

        //
        // user activity
        //
        let newUserActivityListener = this.onUserActivity.bind(this);

        // mouse movement detection
        this.#lastUserActivity = 0;
        window.removeEventListener("mousemove", this.#onUserActivityRef);
        window.addEventListener("mousemove", newUserActivityListener);

        // typing detection
        this.#lastUserActivity = 0;
        window.removeEventListener("keyup", this.#onUserActivityRef);
        window.addEventListener("keyup", newUserActivityListener);
        this.#onVisibilityChangeRef = newOnVisibilityChangedListener

        // test
        if (this.test) {
            let newUserActivityChangeListener = this.onUserActivityChange.bind(this);
            window.removeEventListener("user-activity-status-change", this.#onUserActivityChangeRef);
            window.addEventListener("user-activity-status-change", newUserActivityChangeListener);
            this.#onUserActivityChangeRef = newUserActivityChangeListener;
        }

        this.onVisibilityChange();
    }

    tearDown() {
        if (this.debug)
            console.log("browser-tab-activity.service tearDown");

        // tab visibility 
        document.removeEventListener("visibilitychange", this.#onVisibilityChangeRef);
        
        // mouse movement
        window.removeEventListener("mousemove", this.#onUserActivityRef);
        window.removeEventListener("keyup", this.#onUserActivityRef);

        // test
        if (this.test)
            window.removeEventListener("user-activity-status-change", this.#onUserActivityChangeRef);
    }
}

export default new BrowserTabActivityService()